import React, { useState } from "react";
import { Package as IconoPaquete, Check, RefreshCw } from "lucide-react";
import { Button } from "../shared/Button";
import { paquetes as api } from "../../api/modulos.service";
import { coincide } from "../../utils/texto";
import { estilos as est, Cabecera, Buscador, Vacio, Aviso, Ventana, Entrada, Seleccion, useCarga } from "./base";

/**
 * PAQUETERÍA EN CASETA.
 *
 * El módulo se enciende por residencial y viene apagado: hay casetas que no
 * quieren ni oír hablar de recibir paquetes, y cargarles la pantalla es ruido.
 *
 * Al entregar se anota QUIÉN lo retiró. Sin eso, «yo lo entregué» y «a mí
 * nunca me llegó» son la palabra de uno contra la del otro, que es justo el
 * problema que este módulo viene a quitar de la caseta.
 */

const FORM_VACIO = { houseNumber: "", polygon: "", recipientName: "", carrier: "Otro", trackingNumber: "", description: "" };
const COLOR = { recibido: "var(--gold)", notificado: "var(--warning)", retirado: "var(--success)" };

const fecha = (f) =>
  new Date(f).toLocaleString("es-SV", { timeZone: "America/El_Salvador", dateStyle: "short", timeStyle: "short" });

export default function Paquetes({ rol, catalogos, modulos }) {
  const [busqueda, setBusqueda] = useState("");
  const [abierta, setAbierta] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const { datos, cargando, error: errorCarga, recargar } = useCarga(() => api.listar({ todos: "1" }), []);
  const lista = datos?.packages || [];
  const esCaseta = rol === "guard" || rol === "admin";

  if (modulos && modulos.paquetes === false) {
    return (
      <div style={est.seccion}>
        <Cabecera titulo="Paquetería" subtitulo="Módulo desactivado" icono={IconoPaquete} />
        <Vacio>
          Su residencial no tiene habilitada la paquetería. La administración puede activarla en
          Configuración.
        </Vacio>
      </div>
    );
  }

  const filtrados = busqueda
    ? lista.filter((p) =>
        [p.houseNumber, p.trackingNumber, p.carrier, p.recipientName]
          .filter(Boolean)
          .some((c) => coincide(c, busqueda))
      )
    : lista;

  const cambiar = (c) => (e) => setForm({ ...form, [c]: e.target.value });

  const guardar = async (e) => {
    e.preventDefault();
    setError(""); setGuardando(true);
    try {
      await api.recibir(form);
      setAbierta(false); setForm(FORM_VACIO); recargar();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.errors?.[0]?.msg || "No se pudo registrar");
    } finally { setGuardando(false); }
  };

  const entregar = async (p) => {
    const quien = window.prompt("¿Quién lo retira? (nombre)");
    if (!quien) return;
    try {
      await api.entregar(p._id, { pickedUpBy: quien });
      recargar();
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo registrar la entrega");
    }
  };

  return (
    <div style={est.seccion}>
      <Cabecera
        titulo="Paquetería"
        subtitulo={datos?.pendientes ? `${datos.pendientes} sin retirar.` : "Paquetes recibidos en caseta."}
        accion={esCaseta ? "Recibir paquete" : null}
        alAccionar={() => setAbierta(true)}
        icono={IconoPaquete}
      />

      <Buscador valor={busqueda} alCambiar={setBusqueda} marcador="Buscar por casa, guía o empresa…" />

      {error && <Aviso>{error}</Aviso>}

      {errorCarga ? (
        <Aviso>
          {errorCarga}
          <div style={{ marginTop: "var(--space-3)" }}>
            <Button size="sm" variant="outline" leftIcon={RefreshCw} onClick={recargar}>
              Reintentar
            </Button>
          </div>
        </Aviso>
      ) : cargando ? (
        <Vacio>Cargando…</Vacio>
      ) : filtrados.length === 0 ? (
        <Vacio>No hay paquetes.</Vacio>
      ) : (
        <div style={est.rejilla}>
          {filtrados.map((p) => (
            <div key={p._id} style={{ ...est.tarjeta, borderColor: COLOR[p.status] }}>
              <h3 style={est.tarjetaTitulo}>Casa {p.polygon}-{p.houseNumber}</h3>
              <div style={est.tarjetaDato}>{p.carrier || "Sin empresa"}{p.trackingNumber ? ` · ${p.trackingNumber}` : ""}</div>
              <div style={est.tarjetaDato}>{fecha(p.receivedAt)}</div>
              <div style={{ ...est.tarjetaDato, color: COLOR[p.status], fontWeight: 600 }}>
                {p.status === "retirado" ? `Retirado por ${p.pickedUpBy}` : "Sin retirar"}
              </div>
              {esCaseta && p.status !== "retirado" && (
                <div style={est.tarjetaAcciones}>
                  <Button size="sm" variant="primary" leftIcon={Check} onClick={() => entregar(p)}>
                    Entregar
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Ventana
        titulo="Recibir un paquete"
        abierta={abierta}
        alCerrar={() => setAbierta(false)}
        pie={<>
          <Button variant="ghost" onClick={() => setAbierta(false)}>Cancelar</Button>
          <Button variant="primary" onClick={guardar} loading={guardando}>Registrar</Button>
        </>}
      >
        {error && <Aviso>{error}</Aviso>}
        <div style={est.fila}>
          <Entrada etiqueta="Polígono" value={form.polygon} onChange={cambiar("polygon")} required />
          <Entrada etiqueta="Casa" value={form.houseNumber} onChange={cambiar("houseNumber")} required />
        </div>
        <Entrada etiqueta="Para quién" value={form.recipientName} onChange={cambiar("recipientName")} />
        <div style={est.fila}>
          <Seleccion etiqueta="Empresa" value={form.carrier} onChange={cambiar("carrier")}
            opciones={(catalogos?.empresasEntrega || ["Otro"]).map((e) => ({ valor: e, etiqueta: e }))} />
          <Entrada etiqueta="Número de guía" value={form.trackingNumber} onChange={cambiar("trackingNumber")} />
        </div>
        <Entrada etiqueta="Descripción" value={form.description} onChange={cambiar("description")} placeholder="Caja mediana" />
      </Ventana>
    </div>
  );
}
